
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useSocket } from './SocketContext';

const EmployeeContext = createContext({});


function mapLeave(l) {
  const colors = { 'Casual Leave': '#6366f1', 'Sick Leave': '#ef4444', 'Personal Leave': '#f59e0b', 'Comp Off': '#3b82f6', 'Earned Leave': '#2563eb', 'WFH': '#8b5cf6', 'Loss of Pay': '#dc2626' };
  return { type: l.leave_type || l.type || '', total: l.total || 0, used: l.used || 0, remaining: l.remaining || 0, color: l.color || colors[l.leave_type || l.type] || '#6366f1' };
}

function mapPayslip(p) {
  const basic = Number(p.basic || 0);
  const hra = Number(p.hra || 0);
  const bonus = Number(p.bonus || 0);
  const otherAllowance = Number(p.other_allowance || 0);
  const allowances = Number(p.allowances || 0);
  const totalEarning = Number(p.gross_earning || 0) || (basic + hra + bonus + otherAllowance + allowances);
  const pf = Number(p.pf || 0);
  const esi = Number(p.esi || 0);
  const professionTax = Number(p.profession_tax || 0);
  const otherDed = Number(p.deductions || 0) + Number(p.tax || 0);
  const totalDeduction = Number(p.gross_deduction || 0) || (pf + esi + professionTax + otherDed);
  const net = Number(p.net_pay || 0) || (totalEarning - totalDeduction);
  return {
    month: p.month || '',
    year: p.year || '',
    gross: totalEarning.toLocaleString('en-IN'),
    ded: totalDeduction.toLocaleString('en-IN'),
    net: net.toLocaleString('en-IN'),
    status: p.status || 'paid',
    basic: basic.toLocaleString('en-IN'),
    hra: hra.toLocaleString('en-IN'),
    bonus: bonus.toLocaleString('en-IN'),
    otherAllowance: otherAllowance.toLocaleString('en-IN'),
    pf: pf.toLocaleString('en-IN'),
    esi: esi.toLocaleString('en-IN'),
    professionTax: professionTax.toLocaleString('en-IN'),
    totalEarning: totalEarning.toLocaleString('en-IN'),
    totalDeduction: totalDeduction.toLocaleString('en-IN'),
    rawNet: net,
    rawGross: totalEarning,
    rawDed: totalDeduction
  };
}

const ONBOARDING_CHECKLIST_TEMPLATE = [
  'Personal details completed',
  'Identity documents verified',
  'Address details provided',
  'Education details added',
  'Bank details provided',
  'Documents uploaded',
  'Declaration accepted'
];

const isFilled = (v) => v !== null && v !== undefined && String(v).trim() !== '';

function deriveChecklist(onb = {}) {
  const profile = onb.profile || {};
  const bank = onb.bank_details || {};
  const education = Array.isArray(onb.education) ? onb.education : [];
  const declaration = onb.declaration || null;
  const docs = Array.isArray(onb.documents) ? onb.documents : [];

  const hasPersonal = ['full_name', 'dob', 'phone', 'personal_email'].every((k) => isFilled(profile[k]));
  const hasAddress = ['current_address', 'current_city', 'current_state', 'current_pincode'].every((k) => isFilled(profile[k]));
  const hasBank = ['bank_name', 'account_number', 'ifsc_code'].every((k) => isFilled(bank[k]));
  const hasEducation = education.length > 0;
  const hasDeclaration = Boolean(declaration && (declaration.accepted || declaration.declaration || declaration.declaration_date));

  const docByType = {};
  docs.forEach((d) => {
    const key = String(d.doc_type || '').toLowerCase();
    if (!key) return;
    const prev = docByType[key];
    if (!prev) {
      docByType[key] = d;
      return;
    }
    const prevTs = new Date(prev.uploaded_at || 0).getTime();
    const curTs = new Date(d.uploaded_at || 0).getTime();
    if (curTs >= prevTs) docByType[key] = d;
  });
  const isAcceptedDoc = (docType) => {
    const doc = docByType[docType];
    if (!doc) return false;
    const status = String(doc.status || '').toLowerCase();
    return !status || ['available', 'approved', 'verified', 'pending', 'uploaded'].includes(status);
  };
  const requiredIdentityDocs = ['aadhaar', 'pan', 'photo'];
  const hasIdentityDocs = requiredIdentityDocs.every(isAcceptedDoc);
  const hasAnyDocument = docs.length > 0;

  const doneMap = {
    'Personal details completed': hasPersonal,
    'Identity documents verified': hasIdentityDocs,
    'Address details provided': hasAddress,
    'Education details added': hasEducation,
    'Bank details provided': hasBank,
    'Documents uploaded': hasAnyDocument,
    'Declaration accepted': hasDeclaration
  };

  return ONBOARDING_CHECKLIST_TEMPLATE.map((label) => ({ label, done: Boolean(doneMap[label]) }));
}

export function EmployeeProvider({ children }) {
  const { socket, connected, on, off } = useSocket();
  const [d, setD] = useState({
    loaded: false,
    empInfo: { name: "", id: "", email: "", role: "", team: "", manager: "", startDate: "", location: "" },
    empChecklist: [], empOnboardDocs: [],
    empAttendanceData: { totalDays: 0, present: 0, leaves: 0, holidays: 0 },
    empLeaveSummary: [], empDailyAtt: {}, empMonthlyStats: {},
    empInitLeaveReqs: [], empInitPermReqs: [],
    empCalEvents: [], empNotifications: [],
    empChatMessages: [], empTimeline: [],
    empAssets: [], empSoftware: [], empAITools: [], empCredentials: null,
      empLop: [],
    empTickets: [], empPolicies: [],
    empPayslips: [], empProfileDetails: [], empUploadedDocs: [],
    empPerformance: [], empAdvances: []
  });

  const fetchAll = useCallback(async () => {
    const mo = new Date().getMonth() + 1;
    const yr = new Date().getFullYear();
    const [
      prof, onb, att, leave, leaveReqs, notifs, payslips, timeline, assets, tickets, policies, docs,
      calEv, experience, permBalance, permRequests, meData, myPerformance, advances, myLop
    ] = await Promise.all([
      api.getProfile().catch(() => ({})),
      api.getOnboardingStatus().catch(() => ({})),
      api.getAttendanceSummary().catch(() => ({ totalDays: 0, present: 0, leaves: 0, holidays: 0 })),
      api.getLeaveBalance().catch(() => []),
      api.getMyLeaveRequests().catch(() => []),
      api.getNotifications().catch(() => []),
      api.getMyPayslips().catch(() => []),
      api.getTimeline().catch(() => []),
      api.getAssets().catch(() => ({ assets: [], software: [], aiTools: [] })),
      api.getTickets().catch(() => []),
      api.getPolicies().catch(() => []),
      api.getMyDocs().catch(() => []),
      api.getEvents(mo, yr).catch(() => []),
      api.getExperience().catch(() => null),
      api.getPermissionBalance().catch(() => null),
      api.getMyPermissions().catch(() => []),
      api.getMe().catch(() => ({})), // For performance rating fallback
      api.getMyPerformance().catch(() => []),
      api.getAdvances().catch(() => []), // This will be getMyAdvances soon
      api.getMyLop().catch(() => []),
    ]);
    const profData = prof.profile || prof || {};
    const onbProfile = onb.profile || {};
    const bank = onb.bank_details || {};
    const p = { ...profData, ...onbProfile, ...(bank && typeof bank === 'object' ? bank : {}) };
    const allLeave = leaveReqs || [];
    const assetData = assets || {};
    const checklist = deriveChecklist(onb);
    setD(prev => ({
      ...prev, loaded: true,
      empInfo: (() => {
        const mapped = (payslips || []).map(mapPayslip);
        const latest = mapped[0] || { net: '0', gross: '0', ded: '0' };
        return { name: p.full_name || p.name || '', id: p.emp_id || '', email: p.personal_email || p.email || '', role: p.designation || '', team: p.team || '', manager: p.manager || p.manager_name || '', startDate: p.joining_date || '', location: p.location || '', net: latest.net, gross: latest.gross, ded: latest.ded };
      })(),
      empChecklist: checklist,
      empOnboardDocs: (onb.documents || []).map(d => ({ ...d, name: d.doc_name || d.name })),
      empAttendanceData: att || { totalDays: 0, present: 0, leaves: 0, holidays: 0 },
      empLeaveSummary: (leave || []).map(mapLeave),
      empInitLeaveReqs: allLeave.filter(r => r.request_type !== 'permission').map(r => ({
        ...r,
        type: r.leave_type || r.type || '',
        from: r.from_date || r.from || '',
        to: r.to_date || r.to || '',
        days: r.days || 0,
        reason: r.reason || '',
        status: r.status || 'pending',
        pmStatus: r.pm_status || '',
        hrStatus: r.hr_status || '',
        pmComment: r.pm_comment || '',
        hrComment: r.hr_comment || ''
      })),
      empInitPermReqs: (Array.isArray(permRequests) ? permRequests : []).map(r => ({
        ...r,
        type: 'Permission',
        date: r.date || '',
        fromTime: r.from_time || '',
        toTime: r.to_time || '',
        hours: r.hours || 0,
        reason: r.reason || '',
        status: r.status || 'pending',
        pmStatus: r.pm_status || '',
        hrStatus: r.hr_status || '',
      })),
      empNotifications: notifs || [],
      empPayslips: (payslips || []).map(mapPayslip),
      empTimeline: timeline || [],
      empLop: myLop || [],
      empAssets: assetData.assets || (Array.isArray(assetData) ? assetData : []),
      empSoftware: assetData.software || [],
      empAITools: assetData.aiTools || [],
      empCredentials: assetData.credentials || null,
      empTickets: tickets || [],
      empPolicies: (policies || []).filter((p) => String(p?.status || '').toLowerCase() === 'active'),
      empProfileDetails: (() => {
        const source = { ...(meData || {}), ...p };
        const skillsDisplay = Array.isArray(source.skills)
          ? source.skills.join(', ')
          : String(source.skills || '');
        const asDate = (v) => {
          if (!v) return '-';
          const d = new Date(v);
          return Number.isNaN(d.getTime())
            ? String(v)
            : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        };
        const pick = (...keys) => {
          for (const k of keys) {
            const val = source[k];
            if (val !== undefined && val !== null && String(val).trim() !== '') return val;
          }
          return '';
        };
        return [
          { label: 'Date of Birth', value: String(asDate(pick('dob', 'date_of_birth'))) },
          { label: 'Blood Group', value: String(pick('blood_group', 'bloodGroup') || '-') },
          { label: 'Gender', value: String(pick('gender') || '-') },
          { label: 'Marital Status', value: String(pick('marital_status', 'maritalStatus') || '-') },
          { label: 'Phone', value: String(pick('personal_phone', 'phone', 'mobile') || '-') },
          { label: 'Personal Email', value: String(pick('personal_email', 'other_email', 'email') || '-') },
          { label: 'Emergency Contact', value: String(pick('emergency_contact') || '-') },
          { label: 'PAN Number', value: String(pick('pan_number', 'panNumber') || '-') },
          { label: 'Current Address', value: String(pick('current_address', 'currentAddress', 'address') || '-') },
          { label: 'Current City', value: String(pick('current_city', 'currentCity', 'city') || '-') },
          { label: 'Current State', value: String(pick('current_state', 'currentState', 'state') || '-') },
          { label: 'Current Pincode', value: String(pick('current_pincode', 'currentPincode', 'pincode') || '-') },
          { label: 'Permanent Address', value: String(pick('permanent_address', 'permanentAddress') || '-') },
          { label: 'Permanent City', value: String(pick('permanent_city', 'permanentCity') || '-') },
          { label: 'Permanent State', value: String(pick('permanent_state', 'permanentState') || '-') },
          { label: 'Permanent Pincode', value: String(pick('permanent_pincode', 'permanentPincode') || '-') },
          { label: 'Designation', value: String(pick('designation', 'role') || '-') },
          { label: 'Manager Id', value: String(pick('manager_id', 'managerId', 'manager') || '-') },
          { label: 'Joining Date', value: String(pick('joining_date', 'joiningDate', 'expected_joining_date') || '-') },
          { label: 'End Date', value: String(pick('end_date', 'endDate') || '-') },
          { label: 'Location', value: String(pick('location') || '-') },
          { label: 'Skills', value: skillsDisplay || '-' }
        ];
      })(),
      empUploadedDocs: (docs || []).map(d => ({ id: d.id, name: d.doc_name || d.name, date: d.uploaded_at || d.date, type: d.doc_type })),
      empCalEvents: calEv || [],
      empChatMessages: [],
      empExperience: experience || null,
      empPermBalance: permBalance || null,
      empPerformance: Array.isArray(myPerformance) ? myPerformance : [],
      empAdvances: Array.isArray(advances) ? advances : [],
    }));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Realtime refresh on notifications / module updates
  useEffect(() => {
    if (!connected || !socket) return;
    const refresh = () => { fetchAll().catch(() => { }); };
    const refreshNotifs = () => {
      api.getNotifications().then(data => setD(prev => ({ ...prev, empNotifications: data || [] }))).catch(() => { });
    };
    const onNotification = () => { refreshNotifs(); };
    const onHelpdeskUpdate = () => { refresh(); };
    const onPayrollUpdate = () => { refresh(); };
    const onAssetUpdate = () => { refresh(); };
    const onAssetAssigned = () => {
      api.getAssets().then(assetData => {
        setD(prev => ({
          ...prev,
          empAssets: (assetData?.assets) || (Array.isArray(assetData) ? assetData : []),
          empSoftware: assetData?.software || [],
          empAITools: assetData?.aiTools || [],
          empCredentials: assetData?.credentials || null,
        }));
      }).catch(() => {});
      refreshNotifs();
    };
    const onLeaveUpdate = () => { refresh(); };

    const onLostAssetNotice = () => { refreshNotifs(); };

    on('notification', onNotification);
    on('notification:new', onNotification);
    on('helpdesk:update', onHelpdeskUpdate);
    on('payroll:update', onPayrollUpdate);
    on('asset:update', onAssetUpdate);
    on('asset:assigned', onAssetAssigned);
    on('credential:assigned', onAssetAssigned);
    const onLopUpdate = () => {
      api.getMyLop().then(d => setD(prev => ({ ...prev, empLop: d || [] }))).catch(() => {});
      refreshNotifs();
    };
    on('lop:calculated', onLopUpdate);
    on('lop:notified',   onLopUpdate);
    on('leave:update', onLeaveUpdate);
    on('lost_asset:notice', onLostAssetNotice);

    return () => {
      off('notification', onNotification);
      off('notification:new', onNotification);
      off('helpdesk:update', onHelpdeskUpdate);
      off('payroll:update', onPayrollUpdate);
      off('asset:update', onAssetUpdate);
      off('asset:assigned', onAssetAssigned);
      off('credential:assigned', onAssetAssigned);
      off('lop:calculated', onLopUpdate);
      off('lop:notified',   onLopUpdate);
      off('leave:update', onLeaveUpdate);
      off('lost_asset:notice', onLostAssetNotice);
    };
  }, [connected, socket, on, off, fetchAll]);

  const refreshAssets = useCallback(() => {
    return api.getAssets().then(assetData => {
      setD(prev => ({
        ...prev,
        empAssets: (assetData?.assets) || (Array.isArray(assetData) ? assetData : []),
        empSoftware: assetData?.software || [],
        empAITools: assetData?.aiTools || [],
        empCredentials: assetData?.credentials || null,
      }));
    }).catch(() => {});
  }, []);

  return <EmployeeContext.Provider value={{ ...d, refreshEmployeeData: fetchAll, refreshAssets }}>{children}</EmployeeContext.Provider>;
}

export const useEmployeeData = () => useContext(EmployeeContext);
